// B1.4: shifts domain methods (move-only from client.ts).
import { ApiClientCore, ApiError, buildQueryString } from './http/core';

export class ShiftsApi extends ApiClientCore {
  async listShiftViolations(opts?: {
    locationIds?: number[];
    status?: string;
    violationType?: string;
    employeeId?: number;
    q?: string;
    createdFrom?: string;
    createdTo?: string;
    sort?: 'default' | 'created_desc' | 'created_asc';
    limit?: number;
    offset?: number;
  }): Promise<{ items: any[]; total: number }> {
    const p: Record<string, any> = {};
    if (opts?.locationIds?.length) p.location_ids = opts.locationIds.join(',');
    if (opts?.status) p.status = opts.status;
    if (opts?.violationType) p.violation_type = opts.violationType;
    if (opts?.employeeId != null) p.employee_id = opts.employeeId;
    if (opts?.q) p.q = opts.q;
    if (opts?.createdFrom) p.created_from = opts.createdFrom;
    if (opts?.createdTo) p.created_to = opts.createdTo;
    if (opts?.sort) p.sort = opts.sort;
    if (opts?.limit != null) p.limit = opts.limit;
    if (opts?.offset != null) p.offset = opts.offset;
    const raw = await this.get(`/api/v1/shift-violations${buildQueryString(p)}`);
    if (Array.isArray(raw)) {
      return { items: raw, total: raw.length };
    }
    return {
      items: Array.isArray(raw?.items) ? raw.items : [],
      total: typeof raw?.total === 'number' ? raw.total : 0,
    };
  }
  shiftViolationsExportPath(opts?: {
    locationIds?: number[];
    status?: string;
    violationType?: string;
    employeeId?: number;
    q?: string;
    createdFrom?: string;
    createdTo?: string;
    sort?: 'default' | 'created_desc' | 'created_asc';
  }): string {
    const p: Record<string, any> = {};
    if (opts?.locationIds?.length) p.location_ids = opts.locationIds.join(',');
    if (opts?.status) p.status = opts.status;
    if (opts?.violationType) p.violation_type = opts.violationType;
    if (opts?.employeeId != null) p.employee_id = opts.employeeId;
    if (opts?.q) p.q = opts.q;
    if (opts?.createdFrom) p.created_from = opts.createdFrom;
    if (opts?.createdTo) p.created_to = opts.createdTo;
    if (opts?.sort) p.sort = opts.sort;
    return `/api/v1/shift-violations/export.csv${buildQueryString(p)}`;
  }
  async downloadShiftViolationsCsv(
    opts?: {
      locationIds?: number[];
      status?: string;
      violationType?: string;
      employeeId?: number;
      q?: string;
      createdFrom?: string;
      createdTo?: string;
      sort?: 'default' | 'created_desc' | 'created_asc';
    },
    filename = 'shift_violations.csv'
  ): Promise<void> {
    if (typeof window === 'undefined') return;
    const blob = await this.getBlob(this.shiftViolationsExportPath(opts));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  async getShiftViolationActivity(
    violationId: number
  ): Promise<
    Array<{
      id: number;
      action: string;
      user_id?: number | null;
      details: Record<string, unknown>;
      created_at: string;
      actor_label?: string | null;
    }>
  > {
    return this.get(`/api/v1/shift-violations/${violationId}/activity`);
  }
  async getShiftViolation(id: number): Promise<any> {
    return this.get(`/api/v1/shift-violations/${id}`);
  }
  async acknowledgeShiftViolation(id: number): Promise<any> {
    return this.post(`/api/v1/shift-violations/${id}/acknowledge`, {});
  }
  async resolveShiftViolation(id: number, note?: string | null): Promise<any> {
    return this.post(`/api/v1/shift-violations/${id}/resolve`, { note: note ?? null });
  }
  async dismissShiftViolation(id: number, reasonCode: string, note?: string | null): Promise<any> {
    return this.post(`/api/v1/shift-violations/${id}/dismiss`, {
      reason_code: reasonCode,
      note: note ?? null,
    });
  }
  // ─── Shifts ──────────────────────────────────────────────────────────────────
  async getShifts(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/shifts${buildQueryString(params)}`);
  }
  async getShift(id: number): Promise<any> {
    return this.get(`/api/v1/shifts/${id}`);
  }
  async listShiftTimeCorrections(shiftId: number): Promise<any[]> {
    return this.get(`/api/v1/shifts/${shiftId}/time-corrections`);
  }
  async approveShiftTimeCorrection(correctionId: number, review_notes?: string): Promise<any> {
    return this.post(`/api/v1/shifts/time-corrections/${correctionId}/approve`, {
      review_notes: review_notes ?? null,
    });
  }
  async rejectShiftTimeCorrection(correctionId: number, review_notes?: string): Promise<any> {
    return this.post(`/api/v1/shifts/time-corrections/${correctionId}/reject`, {
      review_notes: review_notes ?? null,
    });
  }
  async downloadShiftReportPdf(shiftId: number): Promise<Blob> {
    const url = `${this.baseURL}/api/v1/shifts/${shiftId}/report.pdf`;
    const response = await this.authedFetch(url, { method: 'GET' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        (errorData as any).detail || (errorData as any).error || response.statusText,
        errorData
      );
    }
    return response.blob();
  }
  async createShift(data: any): Promise<any> {
    return this.post('/api/v1/shifts', data);
  }
  async updateShift(id: number, data: any): Promise<any> {
    return this.put(`/api/v1/shifts/${id}`, data);
  }
  async cancelShift(id: number): Promise<any> {
    return this.post(`/api/v1/shifts/${id}/cancel`);
  }
  async forceCloseShift(
    shiftId: number,
    payload: {
      reason: string;
      employee_was_on_shift?: boolean;
      actual_start_override?: string;
      actual_end_override?: string;
      skip_payroll?: boolean;
    }
  ): Promise<any> {
    return this.post(`/api/v1/shifts/${shiftId}/force-close`, payload);
  }
  async bulkForceCloseShifts(payload: {
    shift_ids: number[];
    reason: string;
    employee_was_on_shift?: boolean;
    skip_payroll?: boolean;
    actual_start_override?: string;
    actual_end_override?: string;
  }): Promise<any> {
    return this.post('/api/v1/shifts/bulk-force-close', payload);
  }
  async getAnalyticsShiftsTrend(days: number, locationId?: number): Promise<any> {
    const params: Record<string, any> = { days };
    if (locationId !== undefined) params.location_id = locationId;
    return this.get(`/api/v1/analytics/charts/shifts-trend${buildQueryString(params)}`);
  }
  // ─── Shift Reports ───────────────────────────────────────────────────────────
  async getShiftReports(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/shift-reports/${buildQueryString(params)}`);
  }
  async getShiftReport(id: number): Promise<any> {
    return this.get(`/api/v1/shift-reports/${id}`);
  }
  async getShiftReportByShift(shiftId: number): Promise<any> {
    return this.get(`/api/v1/shift-reports/shift/${shiftId}`);
  }
  async reviewShiftReport(id: number, data: { status: 'approved' | 'needs_correction' | 'flagged'; review_comment?: string }): Promise<any> {
    return this.put(`/api/v1/shift-reports/${id}/review`, data);
  }
  async getShiftReportEscalations(id: number): Promise<any> {
    return this.get(`/api/v1/shift-reports/${id}/escalations`);
  }
  async getShiftReportHistory(id: number): Promise<any> {
    return this.get(`/api/v1/shift-reports/${id}/history`);
  }
  async createShiftReportTaskEscalation(id: number, data?: { review_comment?: string }): Promise<any> {
    return this.post(`/api/v1/shift-reports/${id}/escalations/task`, data);
  }
  async createShiftReportIncidentEscalation(id: number, data?: { review_comment?: string }): Promise<any> {
    return this.post(`/api/v1/shift-reports/${id}/escalations/incident`, data);
  }
}
